import {
  Divider,
  Select,
  Spin,
  Modal,
  Table,
  Button,
  Form,
  Input,
  Upload,
  message,
  Image,
} from 'antd'
import throttle from 'lodash.throttle'
import { fetchCountryProvider } from 'src/api/authen'
import { isoToTimestamp, longToTimestamp } from 'src/utils/timestamp'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import UserSearch from 'src/components/Search/UserSearch'
import { useEffect, useState, useRef, useCallback } from 'react'
import { getBots } from 'src/api/user'
import { downloadImageById } from 'src/api/image'
import { getMetaverseCategoryById } from 'src/api/metaverse'
import {
  getChatMessages,
  saveMessageUser,
  getChatRooms,
  deleteMessage,
  getPost,
} from 'src/api/social'
import './BotChat.css'
import {
  MessageOutlined,
  SmileOutlined,
  UploadOutlined,
  DeleteOutlined,
  ArrowRightOutlined,
  FileTextOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import EmojiPicker from 'emoji-picker-react'
import AttachmentPreview from 'src/components/AttachmentPreview'

const BotChats = () => {
  const [providerCountry, setProviderCountry] = useState('TN')
  const prevScrollHeightRef = useRef(0)
  const chatScrollRef = useRef(null)
  const [messagePage, setMessagePage] = useState(0)
  const MESSAGE_PAGE_SIZE = 10
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState(true)
  const [botUserId, setBotUserId] = useState(undefined)
  const [loading, setLoading] = useState(true)
  const [form] = Form.useForm()
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const emojiPickerRef = useRef(null)
  const newImages = useRef<File[]>([])
  const [botUserData, setBotUserData] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [username, setUsername] = useState(undefined)
  const [totalRecords, setTotalRecords] = useState(0)
  const [selectedChatRoomId, setSelectedChatRoomId] = useState(null)
  const [attachmentPreviews, setAttachmentPreviews] = useState({})
  const [countries, setCountries] = useState([])

  const [chatMessages, setChatMessages] = useState([])
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [selectedUserName, setSelectedUserName] = useState(null)

  const [newMessage, setNewMessage] = useState('')
  const [attachedFile, setAttachedFile] = useState(null)
  const [fileList, setFileList] = useState([])
  const userSearchRef = useRef(null)
  const [previewPostVisible, setPreviewPostVisible] = useState(false)
  const [selectedPost, setSelectedPost] = useState(null)
  const [postAttachments, setPostAttachments] = useState([])
  const [loadingPost, setLoadingPost] = useState(false)
  const [error, setError] = useState(null)
  const imageExtensions = ['png', 'jpg', 'jpeg', 'webp', 'gif']
  const [metaverseNames, setMetaverseNames] = useState<Record<string, string>>(
    {}
  )

  const allowedFileTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ]

  const resetChatState = useCallback(() => {
    setChatMessages([])
    setMessagePage(0)
    setHasMoreMessages(true)
    setNewMessage('')
    setAttachedFile(null)
    setFileList([])
    newImages.current = []
  }, [])

  const handleRemove = () => {
    setFileList([])
    setAttachedFile(null)
    newImages.current = []
  }

  const fetchPostDetails = async (postId) => {
    try {
      setLoadingPost(true)
      setError(null)
      const response = await getPost(postId)
      const postData = response.data
      if (postData.attachments?.length > 0) {
        const attachments = await Promise.all(
          postData.attachments.map(async (attachment) => {
            try {
              const url = await downloadImageById(attachment.attachmentId)
              return {
                url,
                type: attachment.fileType,
                id: attachment.attachmentId,
              }
            } catch (error) {
              console.error('Error loading attachment:', error)
              return null
            }
          })
        )

        setPostAttachments(attachments.filter((a) => a !== null))
      }
      setSelectedPost(postData)
    } catch (err) {
      setError('Post has been deleted')
      console.error('Error fetching post:', err)
    } finally {
      setLoadingPost(false)
    }
  }

  const fetchMetaverseNames = async () => {
    const metaverseMessages = chatMessages.filter(
      (msg) => msg.messageType === 'METAVERSE' && msg.resourceId
    )
    const unfetchedIds = metaverseMessages
      .map((msg) => msg.resourceId)
      .filter((id) => !metaverseNames[id])

    const uniqueIds = Array.from(new Set(unfetchedIds))

    if (uniqueIds.length > 0) {
      try {
        const responses = await Promise.allSettled(
          uniqueIds.map((id) => getMetaverseCategoryById(id))
        )

        const newNames = responses.reduce((acc, result, index) => {
          const id = uniqueIds[index]
          acc[id] =
            result.status === 'fulfilled'
              ? result.value.data.name
              : 'Unknown Metaverse'
          return acc
        }, {} as Record<string, string>)

        setMetaverseNames((prev) => ({ ...prev, ...newNames }))
      } catch (error) {
        console.error('Error fetching metaverse names:', error)
      }
    }
  }

  const handlePreview = (file) => {
    const url = URL.createObjectURL(file)
    if (file.type === 'application/pdf') {
      const link = document.createElement('a')
      link.href = url
      link.download = file.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      window.open(url, '_blank')
    }
    URL.revokeObjectURL(url)
  }

  const handleDeleteMessage = async (message) => {
    await deleteMessage(botUserId, message)
    setChatMessages((prevMessages) =>
      prevMessages.filter((msg) => msg.id !== message.id)
    )
  }

  const handleBeforeUpload = (file: File) => {
    if (!allowedFileTypes.includes(file.type)) {
      message.error('Unsupported file type')
      return Upload.LIST_IGNORE
    }

    setAttachedFile(file)
    newImages.current = [file]
    return false
  }

  const fetchCountry = async () => {
    try {
      const data = await fetchCountryProvider()
      const countryOptions = data.data.map((item) => ({
        label: item.countryName,
        value: item.countryCode,
      }))
      setCountries(countryOptions)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const handleMessageClick = (msg) => {
    if (msg.messageType === 'SOCIAL_POST') {
      setPreviewPostVisible(true)
      fetchPostDetails(msg.resourceId)
    }
  }

  const getImagePreview = async (attachmentId) => {
    if (attachmentPreviews[attachmentId])
      return attachmentPreviews[attachmentId]

    try {
      const url = await downloadImageById(attachmentId)
      setAttachmentPreviews((prev) => ({ ...prev, [attachmentId]: url }))
      return url
    } catch (error) {
      console.error('Failed to load attachment image:', error)
      return null
    }
  }

  const fetchBotUser = async () => {
    const response = await getBots()
    const usersList = response.data.map((element) => ({
      label: element.username,
      value: element.user_id,
    }))
    if (usersList.length > 0) {
      setBotUserId(usersList[0].value)
      setBotUserData(usersList)
    }
    setLoading(false)
  }

  const handleInputChange = (e) => {
    setBotUserId(e)
  }

  const handleEmojiSelect = (emojiObject) => {
    const textarea = document.getElementById(
      'editTextArea'
    ) as HTMLTextAreaElement
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    setNewMessage((prevContent) => {
      const textBefore = prevContent.slice(0, start)
      const textAfter = prevContent.slice(end)
      const updatedValue = textBefore + emojiObject.emoji + textAfter
      form.setFieldsValue({ bio: updatedValue })
      return updatedValue
    })
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd =
        start + emojiObject.emoji.length
      textarea.focus()
    }, 0)
  }

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination
    setCurrentPage(current)
    setPageSize(pageSize)
    chatRooms(username, current, pageSize)
  }

  const fetchChatMessages = async (page) => {
    if (!selectedChatRoomId) return

    try {
      const response = await getChatMessages(
        selectedChatRoomId,
        page,
        MESSAGE_PAGE_SIZE
      )
      const newMessages = response.data.reverse()

      setChatMessages((prev) => {
        const existingIds = new Set(prev.map((msg) => msg.id))

        if (page === 0) {
          return newMessages
        } else {
          return [...newMessages.filter((m) => !existingIds.has(m.id)), ...prev]
        }
      })

      setHasMoreMessages(page + 1 < response.total_pages)
    } catch (error) {
      console.error('Failed to fetch chat messages:', error)
    }
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !attachedFile) return

    const messageText = newMessage.trim()
    const tempId = `temp-${Date.now()}`
    const tempMessage = {
      id: tempId,
      content: messageText,
      createdBy: botUserId,
      createdOn: new Date().toISOString(),
      messageType: attachedFile ? 'ATTACHMENT' : 'TEXT',
    }

    setChatMessages((prev) => [...prev, tempMessage])

    setLoading(true)
    setShowEmojiPicker(false)
    setNewMessage('')
    setAttachedFile(null)
    setFileList([])
    newImages.current = []

    try {
      await saveMessageUser(
        selectedUserId,
        botUserId,
        messageText,
        attachedFile ? [attachedFile] : [],
        selectedUserName
      )
      await fetchChatMessages(0)
    } catch (error) {
      console.error('Failed to send message:', error)
      setChatMessages((prev) => prev.filter((msg) => msg.id !== tempId))
      message.error('Failed to send message')
    } finally {
      setLoading(false)
    }
  }

  const handleScroll = useCallback(
    throttle(() => {
      const container = chatScrollRef.current
      if (!container) return

      if (
        container.scrollTop <= 100 &&
        hasMoreMessages &&
        !loadingOlderMessages
      ) {
        const nextPage = messagePage + 1
        setMessagePage(nextPage)
        setLoadingOlderMessages(true)
        prevScrollHeightRef.current = container.scrollHeight

        fetchChatMessages(nextPage).finally(() => {
          setLoadingOlderMessages(false)
          setTimeout(() => {
            if (container) {
              const newHeight = container.scrollHeight
              container.scrollTop = newHeight - prevScrollHeightRef.current
            }
          }, 0)
        })
      }
    }, 100),
    [messagePage, loadingOlderMessages, hasMoreMessages, selectedChatRoomId]
  )

  const transformData = async (data) => {
    if (!data) return []
    return await Promise.all(
      data.map(async (item) => {
        const hasMembers =
          Array.isArray(item.members) && item.members.length > 0
        return {
          ...item,
          userId: hasMembers ? item.members[0].userId : 'Unknown',
          username: hasMembers ? item.members[0].username : 'Unknown',
        }
      })
    )
  }

  const chatRooms = async (
    userId = '',
    page = currentPage,
    size = pageSize
  ) => {
    setLoading(true)
    try {
      const response = await getChatRooms(page - 1, size, botUserId, userId)
      const transformed = await transformData(response.data)
      setTransformedData(transformed)
      setTotalRecords(response.total_items || 0)
    } catch (error) {
      console.error('Failed to fetch chat rooms:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleProviderCountryChange = (value) => {
    setProviderCountry(value)
  }

  const closeModal = () => {
    setChatModalVisible(false)
    setSelectedChatRoomId(null)
    setSelectedUserId(null)
    setSelectedUserName(null)
    resetChatState()
  }

  const showChat = async (record) => {
    resetChatState()
    setSelectedUserId(record.userId)
    setSelectedUserName(record?.username)
    setSelectedChatRoomId(record.id)
    setChatModalVisible(true)

    setTimeout(() => {
      fetchChatMessages(0)
    }, 10)
  }

  const [transformedData, setTransformedData] = useState([])
  const [chatModalVisible, setChatModalVisible] = useState(false)

  const columns = [
    {
      title: 'User Name',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <Button
          onClick={() => showChat(record)}
          style={{ backgroundColor: '#1890ff' }}
        >
          <MessageOutlined style={{ color: 'white' }} />
        </Button>
      ),
    },
  ]

  useEffect(() => {
    const load = async () => {
      await fetchBotUser()
      fetchCountry()
    }
    load()
  }, [])

  useEffect(() => {
    if (botUserId) {
      chatRooms('', 1, pageSize)
    }
  }, [botUserId])

  useEffect(() => {
    if (chatModalVisible && selectedChatRoomId) {
      fetchChatMessages(0)
      const interval = setInterval(() => {
        fetchChatMessages(0)
      }, 15000)
      return () => clearInterval(interval)
    }
  }, [chatModalVisible, selectedChatRoomId])

  useEffect(() => {
    const container = chatScrollRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll)
      }
    }
  }, [handleScroll])

  useEffect(() => {
    const container = chatScrollRef.current
    if (container && messagePage === 0) {
      setTimeout(() => {
        container.scrollTop = container.scrollHeight
      }, 50)
    }
  }, [chatMessages, messagePage])

  useEffect(() => {
    fetchMetaverseNames()
  }, [chatMessages])

  useEffect(() => {
    return () => {
      if (attachedFile?.thumbUrl) {
        URL.revokeObjectURL(attachedFile.thumbUrl)
      }
    }
  }, [attachedFile])

  useEffect(() => {
    return () => {
      postAttachments.forEach((attachment) => {
        if (attachment.url.startsWith('blob:')) {
          URL.revokeObjectURL(attachment.url)
        }
      })
    }
  }, [postAttachments])

  return (
    <>
      <Spin spinning={loading}>
        <Select
          placeholder="Bot User"
          options={botUserData}
          onChange={handleInputChange}
          value={botUserId}
          className="search-input"
          showSearch
          optionFilterProp="label"
          style={{ width: '12%' }}
        />

        <Select
          placeholder="Select User Country"
          value={providerCountry}
          onChange={handleProviderCountryChange}
          options={countries}
          style={{ marginLeft: 10, width: '15%' }}
        />

        <div ref={userSearchRef} style={{ display: 'inline-block' }}>
          <UserSearch
            placeholder="Search Username"
            value={username}
            onChange={(value) => {
              setUsername(value)
              const newPage = 1
              const newSize = 10
              setCurrentPage(newPage)
              setPageSize(newSize)
              chatRooms(value, newPage, newSize)
            }}
            country={providerCountry}
          />
        </div>

        <Divider />
        <DndProvider backend={HTML5Backend}>
          <Table
            dataSource={transformedData}
            columns={columns}
            rowClassName={(record) =>
              record.text_bad_content || record.image_bad_content
                ? 'bad-content-row'
                : ''
            }
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              total: totalRecords,
            }}
            onChange={handleTableChange}
          />
        </DndProvider>
      </Spin>

      <Modal
        title={`Chat Details - ${selectedUserName || 'User'}`}
        visible={chatModalVisible}
        onCancel={closeModal}
        footer={null}
        width={700}
      >
        <Spin spinning={loading}>
          <div
            ref={chatScrollRef}
            style={{
              maxHeight: '400px',
              height: '400px',
              overflowY: 'auto',
              marginBottom: 16,
              position: 'relative',
              paddingRight: 20,
              marginRight: -20,
            }}
          >
            {loadingOlderMessages && (
              <div style={{ textAlign: 'center', padding: 8 }}>
                <Spin />
              </div>
            )}

            {chatMessages
              .filter((msg) => !msg.deleted)
              .map((msg, idx) => {
                const isBot = msg.createdBy === botUserId
                const isSocialPost = msg.messageType === 'SOCIAL_POST'

                return (
                  <div
                    key={msg.id || idx}
                    style={{
                      display: 'flex',
                      justifyContent: isBot ? 'flex-end' : 'flex-start',
                      alignItems: 'flex-start',
                      marginBottom: 8,
                      gap: 8,
                      cursor: isSocialPost ? 'pointer' : 'default',
                    }}
                    onClick={() => handleMessageClick(msg)}
                  >
                    {!isBot && <div style={{ width: 24 }} />}

                    <div
                      style={{
                        backgroundColor: isBot ? '#1890ff' : '#f0f0f0',
                        color: isBot ? '#fff' : '#000',
                        padding: 8,
                        borderRadius: 8,
                        maxWidth: '70%',
                        position: 'relative',
                        wordBreak: 'break-word',
                      }}
                    >
                      {msg.messageType === 'ATTACHMENT' && (
                        <div style={{ minHeight: 100 }}>
                          <AttachmentPreview
                            msg={msg}
                            getImagePreview={getImagePreview}
                          />
                          {msg.content && (
                            <div style={{ marginTop: 8 }}>{msg.content}</div>
                          )}
                        </div>
                      )}
                      {msg.messageType === 'MAZE' && (
                        <div
                          style={{
                            fontStyle: 'italic',
                            marginTop: 4,
                            fontSize: '15px',
                            color: 'black',
                            backgroundColor: '#eecd25',
                          }}
                        >
                          Maze Shared By User: {msg.resourceId}
                        </div>
                      )}
                      {msg.messageType === 'GUYS' && (
                        <div
                          style={{
                            fontStyle: 'italic',
                            marginTop: 4,
                            fontSize: '15px',
                            color: 'black',
                            backgroundColor: '#eecd25',
                          }}
                        >
                          Survival Zone Shared By User: {msg.resourceId}
                        </div>
                      )}
                      {msg.messageType === 'METAVERSE' && (
                        <div
                          style={{
                            fontStyle: 'italic',
                            marginTop: 4,
                            fontSize: '15px',
                            color: 'black',
                            backgroundColor: '#eecd25',
                          }}
                        >
                          User Shared Metaverse Room:
                          {metaverseNames[msg.resourceId] ? (
                            metaverseNames[msg.resourceId]
                          ) : (
                            <Spin size="small" style={{ marginLeft: 8 }} />
                          )}
                        </div>
                      )}
                      {msg.messageType === 'GAME' && (
                        <div
                          style={{
                            fontStyle: 'italic',
                            marginTop: 4,
                            fontSize: '15px',
                            color: 'black',
                            backgroundColor: '#eecd25',
                          }}
                        >
                          Game Shared By User: {msg.resourceId}
                        </div>
                      )}
                      {msg.messageType === 'SOCIAL_POST' && (
                        <div>
                          {msg.content && (
                            <div
                              style={{
                                marginBottom: 8,
                                fontSize: 14,
                                color: isBot ? '#fff' : '#000',
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {msg.content}
                            </div>
                          )}
                          <div
                            style={{
                              border: '1px solid #1890ff',
                              borderRadius: 8,
                              padding: 8,
                              width: '100%',
                              cursor: 'pointer',
                            }}
                            onClick={() => handleMessageClick(msg)}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                              }}
                            >
                              <FileTextOutlined style={{ color: '#1890ff' }} />
                              <div>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                  Click to view post
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {msg.messageType !== 'ATTACHMENT' &&
                        msg.messageType !== 'MAZE' &&
                        msg.messageType !== 'GUYS' &&
                        msg.messageType !== 'METAVERSE' &&
                        msg.messageType !== 'GAME' &&
                        msg.messageType !== 'SOCIAL_POST' &&
                        msg.content && (
                          <div style={{ marginTop: 8 }}>{msg.content}</div>
                        )}

                      <div
                        style={{
                          fontStyle: 'italic',
                          marginTop: 8,
                          fontSize: '10px',
                          color: isBot ? '#fff' : 'black',
                          textAlign: 'right',
                        }}
                      >
                        {isoToTimestamp(msg.createdOn)}
                      </div>
                    </div>

                    {isBot && (
                      <Button
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteMessage(msg)}
                        className="delete-btn"
                      />
                    )}
                  </div>
                )
              })}
          </div>

          <div style={{ position: 'relative' }}>
            {showEmojiPicker && (
              <div
                ref={emojiPickerRef}
                style={{
                  position: 'absolute',
                  zIndex: 2,
                  right: 40,
                  bottom: 60,
                }}
              >
                <EmojiPicker onEmojiClick={handleEmojiSelect} />
              </div>
            )}
            <Input.TextArea
              rows={3}
              id="editTextArea"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSendMessage()
                }
              }}
              style={{ paddingRight: 50 }}
            />
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={handleSendMessage}
              style={{
                position: 'absolute',
                right: 8,
                bottom: 8,
                borderRadius: '50%',
                width: 36,
                height: 36,
                padding: 0,
              }}
            />
          </div>

          <div style={{ position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '5px',
              }}
            >
              <Button
                type="text"
                icon={<SmileOutlined />}
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                style={{
                  backgroundColor: '#ffe0bd',
                  color: 'black',
                  borderRadius: '8px',
                  border: '1px solid #ccc',
                  padding: '5px',
                  marginBottom: '5px',
                }}
              />
            </div>

            <div className="bot-chat-wrapper" style={{ marginBottom: 10 }}>
              <Upload
                beforeUpload={handleBeforeUpload}
                maxCount={1}
                listType="picture-card"
                fileList={fileList}
                onChange={({ fileList }) => setFileList(fileList)}
                onRemove={() => {
                  if (attachedFile?.thumbUrl)
                    URL.revokeObjectURL(attachedFile.thumbUrl)
                  handleRemove()
                }}
                onPreview={handlePreview}
                showUploadList={{
                  showRemoveIcon: false,
                  showPreviewIcon: false,
                }}
                itemRender={(_, file) => (
                  <div className="custom-preview">
                    {imageExtensions.includes(file.type?.toLowerCase()) ? (
                      <img
                        src={URL.createObjectURL(file.originFileObj)}
                        alt="preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <div className="document-preview">
                        <FileTextOutlined style={{ fontSize: 24 }} />
                        <span>{file.name}</span>
                      </div>
                    )}

                    <div className="ant-upload-list-item-actions">
                      <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                          e.stopPropagation()
                          handlePreview(file)
                        }}
                      />
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemove()
                        }}
                      />
                    </div>
                  </div>
                )}
              >
                {!fileList.length && (
                  <Button icon={<UploadOutlined />}>Attach File</Button>
                )}
              </Upload>
            </div>
          </div>
        </Spin>
      </Modal>

      {previewPostVisible && (
        <Modal
          title="Post Preview"
          visible={previewPostVisible}
          onCancel={() => {
            setPreviewPostVisible(false)
            setSelectedPost(null)
            setPostAttachments([])
            setError(null)
          }}
          footer={null}
          width={800}
        >
          {loadingPost ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : error ? (
            <div style={{ color: 'red', textAlign: 'center' }}>{error}</div>
          ) : (
            selectedPost && (
              <div style={{ padding: 16 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <h4>
                      Username : {selectedPost.user?.username || 'Unknown User'}
                    </h4>
                    <span style={{ fontSize: 12, color: '#666' }}>
                      {longToTimestamp(selectedPost.created_on)}
                    </span>
                  </div>
                </div>
                {selectedPost.text && (
                  <div
                    style={{
                      marginBottom: 16,
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.5,
                    }}
                  >
                    {selectedPost.text}
                  </div>
                )}

                {postAttachments.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <Image.PreviewGroup>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            'repeat(auto-fill, minmax(100px, 1fr))',
                          gap: 16,
                        }}
                      >
                        {postAttachments.map((attachment, index) => (
                          <div key={index} style={{ position: 'relative' }}>
                            {imageExtensions.includes(
                              attachment.type?.toLowerCase()
                            ) ? (
                              <Image
                                width="100%"
                                src={attachment.url}
                                alt={`Attachment ${index + 1}`}
                                style={{
                                  borderRadius: 8,
                                  objectFit: 'cover',
                                  height: 100,
                                }}
                              />
                            ) : (
                              <a
                                href={attachment.url}
                                download
                                style={{
                                  display: 'block',
                                  padding: 16,
                                  border: '1px solid #ddd',
                                  borderRadius: 8,
                                  textAlign: 'center',
                                }}
                              >
                                <FileTextOutlined style={{ fontSize: 24 }} />
                                <div style={{ marginTop: 8 }}>
                                  Download File ({attachment.type})
                                </div>
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </Image.PreviewGroup>
                  </div>
                )}
              </div>
            )
          )}
        </Modal>
      )}
    </>
  )
}

export default BotChats
